import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  toRef,
  watch,
} from 'vue'
import { Positions } from '@/models/enums'

// REFACTOR HOOK

const debugHook = false

export const usePopover = (props: any) => {
  const show = ref(false)
  const placement: Ref<Positions | null> = ref<Positions | null>(null)
  const disabled = toRef(props, 'disabled') || ref(false)
  const clickable = toRef(props, 'hoverable') || ref(false)
  const transition = computed<string>(() =>
    placement.value ? `fade-${placement.value.split('-')[0]}` : '',
  )
  const visionTimer = ref<NodeJS.Timeout | null>(null)
  const permanent = ref(props.permanent)
  const autoPosition = ref(props.autoposition)

  // Template Refs
  const popover = toRef(props, 'popover') || ref(null)
  const trigger = toRef(props, 'trigger') || ref(null)

  watch(
    () => props.popover,
    (newPopOver) => {
      popover.value = newPopOver
    },
  )
  watch(
    () => props.trigger,
    (newTrigger) => {
      trigger.value = newTrigger
    },
  )

  const position = ref({
    x: 0,
    y: 0,
  })

  function handleMouseEnter(e: Event) {
    if (debugHook) console.log('mouseenter')

    if (disabled.value) {
      return
    }
    if (
      (e.target as HTMLElement) === (popover.value! as HTMLElement) &&
      !clickable.value
    ) {
      return
    }

    showPopover()
    clearTimeout(visionTimer.value!)
  }

  function handleMouseLeave(e: MouseEvent) {
    if (debugHook) console.log('mouseleave')

    if (!permanent.value) {
      visionTimer.value = setTimeout(() => {
        hidePopover(e)
      }, 400)
    }
  }

  function isMouseAllowedIntoElement(event: MouseEvent | PointerEvent) {
    if (!event) return false
    const target =
      (event?.relatedTarget as HTMLElement) ?? (event?.target as HTMLElement)
    if (!target) return false

    if (
      event.type === 'click' &&
      (target === trigger.value || trigger.value.contains(target))
    ) {
      return false
    }

    const conditions = [
      target === popover.value,
      popover.value.contains(target),
      ...(props.allowedMouseOverTransitionElements ?? []).map(
        (allowedElement: string) => !!target?.closest(allowedElement),
      ),
    ]

    if (debugHook) {
      console.log('isMouseAllowedIntoElement conditions', {
        conditions,
        target,
        popover,
        allowedMouseOverTransitionElements:
          props.allowedMouseOverTransitionElements,
      })
    }

    return conditions.some((condition) => condition)
  }

  function hidePopover(e: MouseEvent, force = false) {
    const isTargetInsidePopover = popover.value.contains(e.target)
    e.stopPropagation()
    if (force) {
      show.value = false
    }
    if (show.value) {
      if (isTargetInsidePopover) return
      show.value = false
    }
    if (show.value) {
      const isMouseAllowed = isMouseAllowedIntoElement(e)
      if (debugHook) {
        console.log('hidePopover debug', {
          e,
          popover,
          trigger,
          conditions: isMouseAllowed,
        })
      }

      if (isMouseAllowed) {
        return
      }

      show.value = false
    }
  }

  function showPopover() {
    if (!show.value && !disabled.value) {
      show.value = true
      setPopoverPosition()
    }
  }

  async function setPopoverPosition() {
    await nextTick()
    const popoverTemp = popover.value! as HTMLElement
    const triggerTemp = trigger.value! as HTMLElement

    if (!popoverTemp || !triggerTemp) {
      if (debugHook) {
        console.debug('no popover or trigger', {
          popoverTemp,
          triggerTemp,
        })
      }
      return
    }

    if (autoPosition.value) {
      const [placementResult, positionResult] = calculatePopoverPosition(
        triggerTemp,
        popoverTemp,
        props.placement.value ?? props.placement,
        1,
        true,
        props.arrowCalculation,
      )

      placement.value = placementResult as Positions

      if (debugHook) {
        console.debug('calculatePopoverPosition result', {
          placementResult,
          placement: placement.value,
          placementProp: props.placement.value,
        })
      }
      position.value = positionResult as { x: number; y: number }
    } else if (props.placement) {
      const [, positionResult] = calculatePopoverPosition(
        triggerTemp,
        popoverTemp,
        props.placement.value ?? props.placement,
        1,
        false,
        props.arrowCalculation,
      )
      placement.value = props.placement
      position.value = positionResult as { x: number; y: number }
    }

    if (!props.disableSetPosition || props.isDropdown) {
      const triggerRect = triggerTemp.getBoundingClientRect()
      const popoverRect = popoverTemp
        .querySelector('ul.menu-pop-up')
        ?.getBoundingClientRect()
      //const menuPopUp = popoverTemp.closest('ul.menu-pop-up')
      const isRightAligned = placement.value?.includes('-right')
      const offset =
        triggerTemp.clientWidth +
        (isRightAligned
          ? (popoverRect?.width ?? 0) +
            (triggerRect.width - (popoverRect?.width ?? 0))
          : 0)
      popoverTemp.style.top = `${
        position.value.y
        //- (menuPopUp ? menuPopUp.scrollTop : 0)
      }px`
      popoverTemp.style.left = props.isDropdown
        ? `${
            position.value.x +
            (isRightAligned ? (popoverRect?.width ?? 0) * -1 : 0)
          }px`
        : `${position.value.x}px`
    }
  }

  let onResizeTimeout: NodeJS.Timeout

  function debounceSetPopoverPosition() {
    clearTimeout(onResizeTimeout)

    onResizeTimeout = setTimeout(
      () => requestAnimationFrame(setPopoverPosition),
      400,
    )
  }

  onMounted(() => {
    window.addEventListener('resize', debounceSetPopoverPosition)
    window.addEventListener('scroll', debounceSetPopoverPosition)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', debounceSetPopoverPosition)
    window.removeEventListener('scroll', debounceSetPopoverPosition)
  })

  return {
    show,
    placement,
    disabled,
    clickable,
    transition,
    visionTimer,
    popover,
    trigger,
    position,
    permanent,
    handleMouseEnter,
    handleMouseLeave,
    hidePopover,
    showPopover,
    setPopoverPosition,
    debounceSetPopoverPosition,
    onUnmounted,
  }
}

type PopoverPosition = [Positions, { x: number; y: number }]

function calculatePopoverPosition(
  trigger: Element,
  popover: Element,
  preferredPos: Positions | null = null,
  padding = 15,
  autoposition = true,
  arrowCalculations = true,
): PopoverPosition {
  const triggerRect = trigger.getBoundingClientRect()
  const popoverRect = popover.getBoundingClientRect()
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight
  const arrowOffsetLeft = arrowCalculations ? convertRemToPixels(3) : 0
  const arrowOffsetRight = arrowCalculations ? convertRemToPixels(3) : 0
  const arrowOffsetTop = arrowCalculations ? convertRemToPixels(2) : 0
  const arrowHeight = arrowCalculations ? convertRemToPixels(1) : 0
  const arrowWidth = arrowCalculations ? convertRemToPixels(1) : 0

  const positions = autoposition
    ? [
        ...new Set([
          ...(preferredPos ? [preferredPos] : [Positions.C]),
          Positions.T,
          Positions.B,
          Positions.R,
          Positions.L,
          ...Object.values(Positions),
        ]),
      ]
    : preferredPos
      ? [preferredPos]
      : [Positions.C]

  if (debugHook) {
    console.debug({
      positions,
      preferredPos,
      scrollTop,
      scrollLeft,
      viewportWidth,
      viewportHeight,
      arrowOffsetLeft,
      arrowOffsetRight,
      arrowOffsetTop,
      arrowHeight,
      arrowWidth,
    })
  }

  let top = 0
  let left = 0

  for (const position of positions) {
    top = 0
    left = 0

    switch (position) {
      case Positions.T:
        top =
          triggerRect.top -
          popoverRect.height -
          padding +
          scrollTop -
          arrowHeight
        left =
          triggerRect.left +
          triggerRect.width / 2 -
          popoverRect.width / 2 -
          scrollLeft
        break
      case Positions.RT:
        top =
          triggerRect.top -
          popoverRect.height -
          padding +
          scrollTop +
          arrowHeight +
          arrowOffsetTop
        left = triggerRect.right + padding - scrollLeft + arrowWidth
        break
      case Positions.TR:
        top =
          triggerRect.top -
          popoverRect.height -
          padding +
          scrollTop -
          arrowHeight
        left = triggerRect.right + padding - scrollLeft - arrowOffsetLeft
        break
      case Positions.RB:
        top =
          triggerRect.bottom +
          padding +
          scrollTop -
          arrowHeight -
          arrowOffsetTop
        left = triggerRect.right + padding - scrollLeft + arrowWidth
        break
      case Positions.R:
        top =
          triggerRect.top +
          triggerRect.height / 2 -
          popoverRect.height / 2 +
          scrollTop
        left = triggerRect.right + padding - scrollLeft + arrowWidth
        break
      case Positions.BR:
        top = triggerRect.bottom + padding + scrollTop + arrowHeight
        left = triggerRect.right + padding - scrollLeft - arrowOffsetLeft
        break
      case Positions.BL:
        top = triggerRect.bottom + padding + scrollTop + arrowHeight
        left =
          triggerRect.left -
          popoverRect.width -
          padding -
          scrollLeft +
          arrowOffsetRight
        break
      case Positions.B:
        top = triggerRect.bottom + padding + scrollTop + arrowHeight
        left =
          triggerRect.left +
          triggerRect.width / 2 -
          popoverRect.width / 2 -
          scrollLeft
        break
      case Positions.LB:
        top =
          triggerRect.bottom +
          padding +
          scrollTop -
          arrowHeight -
          arrowOffsetTop
        left =
          triggerRect.left -
          popoverRect.width -
          padding -
          scrollLeft -
          arrowHeight
        break
      case Positions.L:
        top =
          triggerRect.top +
          triggerRect.height / 2 -
          popoverRect.height / 2 +
          scrollTop
        left =
          triggerRect.left -
          popoverRect.width -
          padding -
          scrollLeft -
          arrowWidth
        break
      case Positions.LT:
        top =
          triggerRect.top -
          popoverRect.height -
          padding +
          scrollTop +
          arrowHeight +
          arrowOffsetTop
        left =
          triggerRect.left -
          popoverRect.width -
          padding -
          scrollLeft -
          arrowHeight
        break
      case Positions.TL:
        top =
          triggerRect.top -
          popoverRect.height -
          padding +
          scrollTop -
          arrowHeight
        left =
          triggerRect.left -
          popoverRect.width -
          padding -
          scrollLeft +
          arrowOffsetRight
        break
      default:
        continue
    }

    if (debugHook) {
      console.debug(position, {
        els: {
          popoverRect,
          triggerRect,
        },
        top,
        left,
        viewportHeight,
        viewportWidth,
        scrollTop,
        scrollLeft,
      })
    }
    if (
      !autoposition ||
      (scrollTop < top &&
        scrollLeft < left &&
        top + scrollTop >= padding &&
        top + popoverRect.height <= scrollTop + viewportHeight - padding &&
        left + scrollLeft >= padding &&
        left + popoverRect.width <= scrollLeft + viewportWidth - padding)
    ) {
      if (debugHook) {
        const currentFramePosition = currentFrameAbsolutePosition()
        console.debug('result', {
          position,
          left,
          top,
          currentFramePosition,
          autoposition: !autoposition,
          conditions: {
            '0: !autoposition ||': !autoposition,
            '1: scrollTop < top ||': scrollTop < top,
            '2: scrollLeft < left ||': scrollLeft < left,
            '3: (top + scrollTop >= padding &&': top + scrollTop >= padding,
            '4: top + popoverRect.height <= viewportHeight - padding &&':
              top + popoverRect.height <= viewportHeight - padding,
            '5: left + scrollLeft >= padding &&': left + scrollLeft >= padding,
            '6: left + popoverRect.width <= viewportWidth - padding)':
              left + popoverRect.width <= viewportWidth - padding,
          },
        })
      }

      return [
        position,
        {
          x: left,
          y: top,
        },
      ]
    } else {
      if (debugHook) {
        console.debug(`debug ${position}`, {
          'top + scrollTop >= padding': top >= padding,
          'top + popoverRect.height <= viewportHeight - padding':
            top + popoverRect.height <= viewportHeight - padding,
          'left + scrollLeft >= padding': left >= padding,
          'left + popoverRect.width <= viewportWidth - padding':
            left + popoverRect.width <= viewportWidth - padding,
        })
      }
    }
  }

  // Fallback position (centered)
  const centerTop = (viewportHeight - popoverRect.height) / 2
  const centerLeft = (viewportWidth - popoverRect.width) / 2
  // return `top: ${centerTop}px; left: ${centerLeft}px;`
  return [
    Positions.C,
    {
      x: centerLeft,
      y: centerTop,
    },
  ]
}

function currentFrameAbsolutePosition() {
  let currentWindow = window
  let currentParentWindow
  const positions: Array<{
    x: number
    y: number
    scrollTop: number
    scrollLeft: number
  }> = []
  let rect

  while (currentWindow !== window.top) {
    currentParentWindow = currentWindow.parent as Window & typeof globalThis
    for (let idx = 0; idx < currentParentWindow.frames.length; idx++)
      if (currentParentWindow.frames[idx] === currentWindow) {
        for (const frameElement of currentParentWindow.document.getElementsByTagName(
          'iframe',
        )) {
          const scrollTop =
            currentParentWindow.document.documentElement.scrollTop
          const scrollLeft =
            currentParentWindow.document.documentElement.scrollLeft
          if (frameElement.contentWindow === currentWindow) {
            rect = frameElement.getBoundingClientRect()
            positions.push({ x: rect.x, y: rect.y, scrollTop, scrollLeft })
          }
        }
        currentWindow = currentParentWindow
        break
      }
  }

  return positions.reduce(
    (accumulator, currentValue) => {
      return {
        x: accumulator.x + currentValue.x,
        y: accumulator.y + currentValue.y,
        scrollTop: accumulator.scrollTop + currentValue.scrollTop,
        scrollLeft: accumulator.scrollLeft + currentValue.scrollLeft,
      }
    },
    { x: 0, y: 0, scrollTop: 0, scrollLeft: 0 },
  )
}

function convertRemToPixels(rem: number) {
  const styles = getComputedStyle(document.documentElement)
  const unit = rem * parseFloat(styles.fontSize)

  if (Number.isNaN(unit)) {
    return 1
  }

  return unit
}
